import type { EventingAdapter } from '../types';
import type { EventingContext } from '../../types/context';
import type {
  NormalizedInteraction,
  NormalizedView,
} from '../../types/normalized';
import { lookupTagularMapping } from './tagular-registry';
import { createDefaultTagularClient } from './tagular-client';
import type { TagularBeamClient } from './types';

export type TagularAdapterErrorHandler = (
  error: unknown,
  details: { eventKey: string; kind: 'interaction' | 'view' },
) => void;

export interface TagularAdapterOptions {
  readonly client?: TagularBeamClient;
  readonly onError?: TagularAdapterErrorHandler;
  readonly onUnknownEventKey?: (
    eventKey: string,
    kind: 'interaction' | 'view',
  ) => void;
}

export class TagularAdapter implements EventingAdapter {
  readonly name = 'tagular';

  private readonly client: TagularBeamClient;
  private readonly onError: TagularAdapterErrorHandler;
  private readonly onUnknownEventKey?: TagularAdapterOptions['onUnknownEventKey'];

  constructor(options: TagularAdapterOptions = {}) {
    this.client = options.client ?? createDefaultTagularClient();
    this.onError =
      options.onError ??
      ((error, details) => {
        console.warn(
          `[eventing:tagular] Failed to dispatch ${details.kind} "${details.eventKey}":`,
          error,
        );
      });
    this.onUnknownEventKey = options.onUnknownEventKey;
  }

  handleInteraction(
    interaction: NormalizedInteraction,
    context: EventingContext,
  ): void {
    this.dispatch(interaction, context, 'interaction');
  }

  handleView(view: NormalizedView, context: EventingContext): void {
    this.dispatch(view, context, 'view');
  }

  private dispatch(
    event: NormalizedInteraction | NormalizedView,
    context: EventingContext,
    kind: 'interaction' | 'view',
  ): void {
    try {
      const mapping = lookupTagularMapping(event.eventKey, kind);
      if (!mapping) {
        this.onUnknownEventKey?.(event.eventKey, kind);
        return;
      }

      if (!this.client.isAvailable()) {
        return;
      }

      const payload = mapping.buildPayload(event, context);
      this.client.beam(mapping.tagularWireName, payload);
    } catch (error) {
      this.onError(error, { eventKey: event.eventKey, kind });
    }
  }
}
